import React, { useMemo, useState, useRef } from "react";
import { Tabs, Tab, Typography, Box, TextField, IconButton, Divider, Tooltip, MenuItem, Select, FormControl, InputLabel, Switch, FormControlLabel, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import GGLoaderButton from "../../../components/CommonComponents/GGLoaderButton/GGLoaderButton";
import SnackBar, { SnackTypes } from "../../../components/UtilityComponents/SnackBar/SnackBar";
import GGModalWindow from "../../../components/UtilityComponents/GGModalWindow/GGModalWindow";
import { AdminAPIHandler } from "../../../services/NetworkClient/BackendAPIs/AdminAPIHandler";
import { SellerPlan } from "../../../Models/SellerPlan";

const AdminSellerPlansPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [isLoadingActive, setIsLoadingActive] = useState(false);
  const [isLoadingById, setIsLoadingById] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [plans, setPlans] = useState<SellerPlan[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingPlan, setEditingPlan] = useState<SellerPlan | null>(null);
  const [planToDelete, setPlanToDelete] = useState<SellerPlan | null>(null);
  const [fetchType, setFetchType] = useState<'all' | 'active' | 'byId' | null>(null);
  const [idSearch, setIdSearch] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  
  const [formState, setFormState] = useState<any>({
    _id: "",
    name: "",
    description: "",
    sellerPlatformFees: {
      feesPercent: 0,
      maxCap: null,
    },
    termsAndConditions: [""] as string[],
    isActive: true,
    billingCycle: "monthly",
    price: 0,
    maxGigsAllowed: 2,
    maxOrdersPerMonth: null,
    prioritySupport: false,
    features: [""] as string[],
  });

  // Snackbar
  const snackBarRef = useRef<{ showSnackbar(): any }>(null);
  const [snackBarParams, setSnackBarParams] = useState({ message: "", state: SnackTypes.success });
  const notify = (message: string, state: SnackTypes) => { 
    setSnackBarParams({ message, state }); 
    snackBarRef.current?.showSnackbar(); 
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setActiveTab(newValue);

  const fetchAllPlans = async () => {
    setIsLoadingAll(true);
    try {
      const all = await AdminAPIHandler.shared.getAllSellerPlans(false);
      setPlans(all);
      setFetchType('all');
      notify(`Fetched ${all.length} total seller plans`, SnackTypes.success);
    } catch (e) {
      notify("Failed to load all seller plans", SnackTypes.error);
    }
    setIsLoadingAll(false);
  };

  const fetchActivePlans = async () => {
    setIsLoadingActive(true);
    try {
      const active = await AdminAPIHandler.shared.getActiveSellerPlans();
      setPlans(active);
      setFetchType('active');
      notify(`Fetched ${active.length} active seller plans`, SnackTypes.success);
    } catch (e) {
      notify("Failed to load active seller plans", SnackTypes.error);
    }
    setIsLoadingActive(false);
  };

  const fetchPlanById = async () => {
    if (!idSearch.trim()) {
      notify("Please enter a plan ID", SnackTypes.error);
      return;
    }
    setIsLoadingById(true);
    try {
      const plan = await AdminAPIHandler.shared.getSellerPlanById(idSearch.trim());
      setPlans([plan]);
      setFetchType('byId');
      notify(`Found seller plan: ${plan.name}`, SnackTypes.success);
    } catch (e) {
      notify(`Seller plan with ID "${idSearch}" not found`, SnackTypes.error);
      setPlans([]);
      setFetchType('byId');
    }
    setIsLoadingById(false);
  };

  const clearList = () => {
    setPlans([]);
    setFetchType(null);
    setSearchQuery("");
    setIdSearch("");
    notify("List cleared", SnackTypes.success);
  };

  const filteredPlans = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return plans;
    return plans.filter(p =>
      p.name.toLowerCase().includes(q) ||
      (p.description ?? "").toLowerCase().includes(q) ||
      p.id.toLowerCase().includes(q)
    );
  }, [searchQuery, plans]);

  const resetForm = () => setFormState({
    _id: "",
    name: "",
    description: "",
    sellerPlatformFees: {
      feesPercent: 0,
      maxCap: null,
    },
    termsAndConditions: [""],
    isActive: true,
    billingCycle: "monthly",
    price: 0,
    maxGigsAllowed: null,
    maxOrdersPerMonth: null,
    prioritySupport: false,
    features: [""],
  });

  const startCreate = () => { 
    resetForm(); 
    setIsCreateMode(true);
    setEditingPlan(null);
    setShowEditModal(true); 
  };

  const startEdit = (p: SellerPlan) => { 
    setFormState({
      _id: p.id,
      name: p.name,
      description: p.description ?? "",
      sellerPlatformFees: {
        feesPercent: p.sellerPlatformFees.feesPercent,
        maxCap: p.sellerPlatformFees.maxCap ?? null,
      },
      termsAndConditions: (p.termsAndConditions && p.termsAndConditions.length > 0) ? p.termsAndConditions : [""],
      isActive: p.isActive,
      billingCycle: p.billingCycle ?? "monthly",
      price: p.price ?? 0,
      maxGigsAllowed: p.maxGigsAllowed ?? null,
      maxOrdersPerMonth: p.maxOrdersPerMonth ?? null,
      prioritySupport: p.prioritySupport ?? false,
      features: (p.features && p.features.length > 0) ? p.features : [""],
    }); 
    setIsCreateMode(false);
    setEditingPlan(p);
    setShowEditModal(true); 
  };

  const cancelEdit = () => { 
    setShowEditModal(false); 
    setEditingPlan(null);
    setIsCreateMode(false);
    resetForm(); 
  };

  // Helper functions for array fields
  const addFeature = () => {
    setFormState((s: any) => ({ ...s, features: [...s.features, ""] }));
  };

  const removeFeature = (index: number) => {
    setFormState((s: any) => ({
      ...s,
      features: s.features.filter((_: any, i: number) => i !== index)
    }));
  };

  const updateFeature = (index: number, value: string) => {
    setFormState((s: any) => ({
      ...s,
      features: s.features.map((f: string, i: number) => (i === index ? value : f))
    }));
  };

  const addTerm = () => {
    setFormState((s: any) => ({ ...s, termsAndConditions: [...s.termsAndConditions, ""] }));
  };

  const removeTerm = (index: number) => {
    setFormState((s: any) => ({
      ...s,
      termsAndConditions: s.termsAndConditions.filter((_: any, i: number) => i !== index)
    }));
  };

  const updateTerm = (index: number, value: string) => {
    setFormState((s: any) => ({
      ...s,
      termsAndConditions: s.termsAndConditions.map((t: string, i: number) => (i === index ? value : t))
    }));
  };

  const savePlan = async () => {
    const payload = { 
      ...formState,
      termsAndConditions: (formState.termsAndConditions || [])
        .map((s: string) => s.trim())
        .filter((s: string) => s !== ""),
      features: (formState.features || [])
        .map((s: string) => s.trim())
        .filter((s: string) => s !== ""),
      sellerPlatformFees: {
        feesPercent: Number(formState.sellerPlatformFees.feesPercent),
        maxCap: formState.sellerPlatformFees.maxCap ? Number(formState.sellerPlatformFees.maxCap) : null,
      },
      price: Number(formState.price),
      maxGigsAllowed: formState.maxGigsAllowed ? Number(formState.maxGigsAllowed) : null,
      maxOrdersPerMonth: formState.maxOrdersPerMonth ? Number(formState.maxOrdersPerMonth) : null,
    };

    setIsLoadingSave(true);
    try {
      if (isCreateMode) {
        const created = await AdminAPIHandler.shared.createSellerPlan(payload);
        setPlans(prev => [created, ...prev]);
        notify("Seller plan created successfully", SnackTypes.success);
      } else if (editingPlan) {
        const updated = await AdminAPIHandler.shared.updateSellerPlan(editingPlan.id, payload);
        setPlans(prev => prev.map(p => p.id === editingPlan.id ? updated : p));
        notify("Seller plan updated successfully", SnackTypes.success);
      }
      cancelEdit();
    } catch (e: any) {
      notify(e?.message || "Save failed", SnackTypes.error);
    }
    setIsLoadingSave(false);
  };

  const openDeleteConfirmation = (plan: SellerPlan) => {
    setPlanToDelete(plan);
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setPlanToDelete(null);
  };

  const confirmDelete = async () => {
    if (!planToDelete) return;
    
    setIsLoadingDelete(true);
    try {
      await AdminAPIHandler.shared.deleteSellerPlan(planToDelete.id);
      setPlans(prev => prev.filter(p => p.id !== planToDelete.id));
      notify("Seller plan deleted successfully", SnackTypes.success);
      cancelDelete();
    } catch (e: any) {
      notify(e?.message || "Delete failed", SnackTypes.error);
    }
    setIsLoadingDelete(false);
  };

  return (
    <div className="admin-order-page">
      <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={4000} message={snackBarParams.message} />
      
      {/* Compact Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        color: 'white',
        p: 2,
        borderRadius: 2,
        mb: 1,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
          💼 Seller Plans Dashboard
        </Typography>
      </Box>

      {/* Main Content Card */}
      <Box sx={{
        background: 'white',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.05)'
      }}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            background: '#f8fafc',
            px: 3,
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              height: 3,
              borderRadius: '2px 2px 0 0'
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#64748b',
              '&.Mui-selected': {
                color: '#1e293b'
              }
            }
          }}
        >
          <Tab label="📊 Manage Seller Plans" />
      </Tabs>

        <Box sx={{ p: 2 }}>
        {activeTab === 0 && (
          <Box>

            {/* Compact Fetch Controls */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              alignItems: 'center', 
              mb: 3, 
              flexWrap: 'wrap',
              p: 2,
              background: '#f8fafc',
              borderRadius: 2,
              border: '1px solid #e2e8f0'
            }}>
              <GGLoaderButton 
                isLoading={isLoadingAll} 
                onClick={fetchAllPlans} 
                variant="contained"
                size="small"
                sx={{ 
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                  }
                }}
              >
                📋 All Plans
              </GGLoaderButton>
              
              <GGLoaderButton 
                isLoading={isLoadingActive} 
                onClick={fetchActivePlans} 
                variant="contained"
                size="small"
                sx={{ 
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                  }
                }}
              >
                ✅ Active Only
              </GGLoaderButton>

              <GGLoaderButton 
                onClick={startCreate} 
                variant="contained" 
                isLoading={false}
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'
                  }
                }}
              >
                <AddIcon sx={{ mr: 0.5 }} />New Plan
              </GGLoaderButton>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
                <TextField 
                  size="small" 
                  placeholder="Search by plan ID" 
                  value={idSearch} 
                  onChange={(e) => setIdSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && fetchPlanById()}
                  sx={{ minWidth: '200px' }}
                />
                <IconButton 
                  onClick={fetchPlanById} 
                  disabled={!idSearch.trim()}
                  sx={{ 
                    background: '#3b82f6',
                    color: 'white',
                    '&:hover': {
                      background: '#2563eb'
                    },
                    '&:disabled': {
                      background: '#e2e8f0',
                      color: '#94a3b8'
                    }
                  }}
                >
                  <SearchIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Status Panel */}
            {fetchType && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 2,
                p: 1.5, 
                borderRadius: 2, 
                background: fetchType === 'active' ? '#e8f5e8' : fetchType === 'all' ? '#e3f2fd' : '#f3e8ff',
                border: `1px solid ${fetchType === 'active' ? '#4caf50' : fetchType === 'all' ? '#2196f3' : '#8b5cf6'}`,
                minHeight: '40px'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {fetchType === 'all' ? '📋 All Seller Plans' : 
                   fetchType === 'active' ? '✅ Active Seller Plans' : 
                   `🔍 Search Result: "${idSearch}"`} • {plans.length} items
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {<GGLoaderButton 
                      variant="outlined" 
                      onClick={clearList}
                      isLoading={false}
                      size="small"
                      sx={{ color: '#64748b', borderColor: '#d1d5db' }}
                    >
                      Clear
                    </GGLoaderButton>
                  }
                </Box>
              </Box>
            )}

            {/* Search Filter */}
            {plans.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <TextField 
                  size="small" 
                  placeholder="Filter current results..." 
                  value={searchQuery} 
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ width: '300px' }}
                />
              </Box>
            )}

            {/* List */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 2 }}>
              {(isLoadingAll || isLoadingActive || isLoadingById) && plans.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 4, 
                  color: '#64748b', 
                  fontSize: '1.1rem',
                  background: '#f8fafc',
                  borderRadius: 2,
                  border: '1px solid #e2e8f0'
                }}>
                  Loading seller plans...
                </Box>
              ) : filteredPlans.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 4, 
                  color: '#64748b',
                  background: '#f8fafc',
                  borderRadius: 2,
                  border: '1px solid #e2e8f0'
                }}>
                  <Typography variant="h6" sx={{ mb: 1, opacity: 0.5 }}>💼</Typography>
                  <Typography variant="h6" sx={{ mb: 1 }}>No seller plans found</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>Try fetching plans or create a new one</Typography>
                </Box>
              ) : (
                filteredPlans.map(p => (
                  <Box key={p.id} sx={{ 
                    borderLeft: '2px solid',
                    borderColor: fetchType === 'active' ? '#10b981' : fetchType === 'all' ? '#3b82f6' : '#8b5cf6',
                    borderRadius: 3, 
                    p: 2, 
                    background: 'white', 
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                      transform: 'translateY(-4px)'
                    },
                    ...(fetchType === 'active' && p.isActive && {
                      background: 'linear-gradient(135deg, rgba(16,185,129,0.02) 0%, rgba(255,255,255,1) 100%)'
                    }),
                    ...(fetchType === 'all' && {
                      background: 'linear-gradient(135deg, rgba(59,130,246,0.02) 0%, rgba(255,255,255,1) 100%)'
                    })
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1, pr: 2 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#1e293b' }}>{p.name}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                          {p.description || 'No description provided'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Plan">
                          <IconButton 
                            size="small" 
                            onClick={() => startEdit(p)}
                            sx={{ 
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              '&:hover': { 
                                background: '#e3f2fd',
                                borderColor: '#3b82f6'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Plan">
                          <IconButton 
                            size="small" 
                            onClick={() => openDeleteConfirmation(p)}
                            sx={{ 
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              '&:hover': { 
                                background: '#ffebee',
                                borderColor: '#ef4444'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                      gap: 1, 
                      mt: 2,
                      p: 1,
                      background: '#f8fafc',
                      borderRadius: 2,
                      border: '1px solid #e2e8f0'
                    }}>
                      <DataRow label="Plan ID" value={p.id} />
                      <DataRow label="Platform Fees" value={`${p.sellerPlatformFees.feesPercent}%`} />
                      <DataRow label="Max Cap" value={p.sellerPlatformFees.maxCap ? `₹${(p.sellerPlatformFees.maxCap / 100).toFixed(2)}` : '—'} />
                      <DataRow label="Billing Cycle" value={p.billingCycle || '—'} />
                      <DataRow label="Price" value={p.price ? `₹${(p.price / 100).toFixed(2)}` : '—'} />
                      <DataRow label="Max Gigs" value={p.maxGigsAllowed ?? 'Unlimited'} />
                      <DataRow label="Max Orders/Month" value={p.maxOrdersPerMonth ?? 'Unlimited'} />
                      <DataRow label="Priority Support" value={p.prioritySupport ? 'Yes' : 'No'} />
                      <DataRow label="Status" value={p.isActive ? '✅ Active' : '❌ Inactive'} />
                    </Box>
                    
                    {(p.features && p.features.length > 0) && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 1, display: 'block' }}>
                          ✨ Features:
                        </Typography>
                        <Box
                          component="ol"
                          sx={{
                            m: 0,
                            pl: 2.5,
                            color: '#1e293b',
                            '& li': {
                              fontSize: '0.875rem',
                              lineHeight: 1.6,
                              mb: 0.5,
                              '&::marker': {
                                color: '#3b82f6',
                                fontWeight: 600
                              }
                            }
                          }}
                        >
                          {p.features.map((f, i) => (
                            <li key={i}>{f}</li>
                          ))}
                        </Box>
                      </>
                    )}
                    
                    {(p.termsAndConditions && p.termsAndConditions.length > 0) && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 1, display: 'block' }}>
                          📜 Terms & Conditions:
                        </Typography>
                        <Box
                          component="ol"
                          sx={{
                            m: 0,
                            pl: 2.5,
                            color: '#1e293b',
                            '& li': {
                              fontSize: '0.875rem',
                              lineHeight: 1.6,
                              mb: 0.5,
                              '&::marker': {
                                color: '#f59e0b',
                                fontWeight: 600
                              }
                            }
                          }}
                        >
                          {p.termsAndConditions.map((t, i) => (
                            <li key={i}>{t}</li>
                          ))}
                        </Box>
                      </>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </Box>
        )}
        </Box>
      </Box>

      {/* Create/Edit Modal */}
      <GGModalWindow 
        open={showEditModal} 
        onCloseHandler={cancelEdit}
        title={isCreateMode ? "✨ Create Seller Plan" : "✏️ Edit Seller Plan"}
        childView={
          <Box sx={{ minWidth: { xs: '100%', sm: '600px', md: '700px' }, maxHeight: '80vh', overflow: 'auto' }}>
            {/* Plan ID Banner */}
            <Box sx={{
              background: isCreateMode 
                ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: 'white',
              p: 2,
              borderRadius: 2,
              mb: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
                  {isCreateMode ? 'New Plan ID' : 'Plan ID'}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                  {formState._id || 'Not set'}
                </Typography>
              </Box>
              <Box sx={{
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 1,
                px: 1.5,
                py: 0.5
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {isCreateMode ? 'CREATE MODE' : 'EDIT MODE'}
                </Typography>
              </Box>
            </Box>

            {/* Form Content */}
            <Box sx={{ px: 1 }}>
              {/* Basic Information Section */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  📋 Basic Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Plan ID"
                    value={formState._id}
                    onChange={e => setFormState((s: any) => ({ ...s, _id: e.target.value }))}
                    disabled={!isCreateMode}
                    fullWidth
                    size="small"
                    helperText={isCreateMode ? "Unique identifier for the plan (e.g., 'basic', 'pro', 'enterprise')" : "Cannot change plan ID"}
                    required
                  />
                  <TextField
                    label="Plan Name"
                    value={formState.name}
                    onChange={e => setFormState((s: any) => ({ ...s, name: e.target.value }))}
                    fullWidth
                    size="small"
                    required
                  />
                  <TextField
                    label="Description"
                    value={formState.description}
                    onChange={e => setFormState((s: any) => ({ ...s, description: e.target.value }))}
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                  />
                </Box>
              </Box>

              {/* Platform Fees Section */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  💰 Platform Fees
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField
                    label="Seller Platform Fees (%)"
                    type="number"
                    value={formState.sellerPlatformFees.feesPercent}
                    onChange={e => setFormState((s: any) => ({
                      ...s,
                      sellerPlatformFees: { ...s.sellerPlatformFees, feesPercent: Number(e.target.value) }
                    }))}
                    helperText="Platform fee percentage (0-100)"
                    fullWidth
                    size="small"
                    required
                  />
                  <TextField
                    label="Max Cap (in paisa)"
                    type="number"
                    value={formState.sellerPlatformFees.maxCap ?? ""}
                    onChange={e => setFormState((s: any) => ({
                      ...s,
                      sellerPlatformFees: { ...s.sellerPlatformFees, maxCap: e.target.value ? Number(e.target.value) : null }
                    }))}
                    helperText="Leave empty for no cap"
                    fullWidth
                    size="small"
                  />
                </Box>
              </Box>

              {/* Pricing & Billing Section */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  💳 Pricing & Billing
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Billing Cycle</InputLabel>
                    <Select
                      label="Billing Cycle"
                      value={formState.billingCycle}
                      onChange={(e) => setFormState((s: any) => ({ ...s, billingCycle: e.target.value }))}
                    >
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="quarterly">Quarterly</MenuItem>
                      <MenuItem value="yearly">Yearly</MenuItem>
                      <MenuItem value="one-time">One-Time</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Price (in paisa)"
                    type="number"
                    value={formState.price}
                    onChange={e => setFormState((s: any) => ({ ...s, price: Number(e.target.value) }))}
                    helperText={`₹${(formState.price / 100).toFixed(2)}`}
                    fullWidth
                    size="small"
                  />
                </Box>
              </Box>

              {/* Limits Section */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  🎯 Plan Limits
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField
                    label="Max Gigs Allowed"
                    type="number"
                    value={formState.maxGigsAllowed ?? ""}
                    onChange={e => setFormState((s: any) => ({ ...s, maxGigsAllowed: e.target.value ? Number(e.target.value) : null }))}
                    helperText="Leave empty for unlimited"
                    fullWidth
                    size="small"
                  />

                  <TextField
                    label="Max Orders Per Month"
                    type="number"
                    value={formState.maxOrdersPerMonth ?? ""}
                    onChange={e => setFormState((s: any) => ({ ...s, maxOrdersPerMonth: e.target.value ? Number(e.target.value) : null }))}
                    helperText="Leave empty for unlimited"
                    fullWidth
                    size="small"
                  />
                </Box>
              </Box>

              {/* Settings Section */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  ⚙️ Settings
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formState.isActive}
                        onChange={(e) => setFormState((s: any) => ({ ...s, isActive: e.target.checked }))}
                      />
                    }
                    label="Is Active"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formState.prioritySupport}
                        onChange={(e) => setFormState((s: any) => ({ ...s, prioritySupport: e.target.checked }))}
                      />
                    }
                    label="Priority Support"
                  />
                </Box>
              </Box>

              {/* Features Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    ✨ Features
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addFeature}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      color: '#3b82f6'
                    }}
                  >
                    Add Feature
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {formState.features.map((feature: string, index: number) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <TextField
                        value={feature}
                        onChange={(e) => updateFeature(index, e.target.value)}
                        placeholder={`Feature ${index + 1} (e.g., Unlimited revisions)`}
                        fullWidth
                        size="small"
                        multiline
                        maxRows={3}
                      />
                      {formState.features.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => removeFeature(index)}
                          sx={{
                            color: '#ef4444',
                            '&:hover': { background: '#fee2e2' }
                          }}
                        >
                          <RemoveCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Terms & Conditions Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    📜 Terms & Conditions
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addTerm}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      color: '#3b82f6'
                    }}
                  >
                    Add Term
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {formState.termsAndConditions.map((term: string, index: number) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <TextField
                        value={term}
                        onChange={(e) => updateTerm(index, e.target.value)}
                        placeholder={`Term ${index + 1} (e.g., Payment is non-refundable)`}
                        fullWidth
                        size="small"
                        multiline
                        maxRows={3}
                      />
                      {formState.termsAndConditions.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => removeTerm(index)}
                          sx={{
                            color: '#ef4444',
                            '&:hover': { background: '#fee2e2' }
                          }}
                        >
                          <RemoveCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                pt: 2,
                borderTop: '1px solid #e2e8f0'
              }}>
                <Button
                  variant="outlined"
                  onClick={cancelEdit}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#cbd5e1',
                    color: '#64748b',
                    '&:hover': {
                      borderColor: '#94a3b8',
                      background: '#f8fafc'
                    }
                  }}
                >
                  Cancel
                </Button>
                <GGLoaderButton
                  variant="contained"
                  onClick={savePlan}
                  isLoading={isLoadingSave}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    background: isCreateMode
                      ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                      : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    '&:hover': {
                      background: isCreateMode
                        ? 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'
                        : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                    }
                  }}
                >
                  {isCreateMode ? 'Create Plan' : 'Save Changes'}
                </GGLoaderButton>
              </Box>
            </Box>
          </Box>
        }
      />

      {/* Delete Confirmation Modal */}
      <GGModalWindow
        open={showDeleteModal}
        onCloseHandler={cancelDelete}
        title="⚠️ Delete Seller Plan"
        childView={
          <Box sx={{ minWidth: { xs: '100%', sm: '400px' } }}>
            {/* Warning Banner */}
            <Box sx={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              p: 3,
              borderRadius: 2,
              mb: 3,
              textAlign: 'center'
            }}>
              <WarningAmberIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h6" fontWeight={600}>
                Confirm Deletion
              </Typography>
            </Box>

            {/* Deletion Details */}
            <Box sx={{ px: 1, mb: 3 }}>
              <Typography variant="body1" sx={{ mb: 2, color: '#64748b' }}>
                Are you sure you want to delete the following seller plan?
              </Typography>
              
              <Box sx={{
                p: 2,
                background: '#fff3cd',
                border: '2px solid #fbbf24',
                borderRadius: 2,
                mb: 2
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#92400e' }}>
                  Plan Name: {planToDelete?.name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#92400e', fontFamily: 'monospace' }}>
                  Plan ID: {planToDelete?.id}
                </Typography>
              </Box>

              <Box sx={{
                p: 2,
                background: '#fee2e2',
                borderRadius: 2,
                border: '1px solid #ef4444'
              }}>
                <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 600 }}>
                  ⚠️ Warning: This action cannot be undone. Any sellers currently subscribed to this plan may be affected.
                </Typography>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              pt: 2,
              borderTop: '1px solid #e2e8f0'
            }}>
              <Button
                variant="outlined"
                onClick={cancelDelete}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#cbd5e1',
                  color: '#64748b',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    background: '#f8fafc'
                  }
                }}
              >
                Cancel
              </Button>
              <GGLoaderButton
                variant="contained"
                onClick={confirmDelete}
                isLoading={isLoadingDelete}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                  }
                }}
              >
                Delete Plan
              </GGLoaderButton>
            </Box>
          </Box>
        }
      />
    </div>
  );
};

const DataRow = ({ label, value }: { label: string, value: any }) => (
  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
    <Typography variant="caption" color="text.secondary">{label}:</Typography>
    <Typography variant="body2" fontWeight={600}>{String(value)}</Typography>
  </Box>
);

export default AdminSellerPlansPage;
